import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';

// The default pill background is translucent white, which only shows up on
// the colored variants - a tone gives it its own readable colors.
const PILL_TONES = {
  up: { background: '#E2F4E6', color: '#399351' },
  down: { background: '#FBDADA', color: '#EB4747' },
  neutral: { background: '#F4F4F6', color: '#646777' },
};

const DisplayCounter = ({
  id,
  title,
  value,
  text,
  icon,
  color,
  outline = false,
  small = false,
  pill,
  pillTone,
  pillTitle,
  progress,
}) => {
  let colors = {};
  switch (color) {
    case 'primary':
      colors = {
        outline: '#D7E9FE',
        outlineColor: '#0469dc',
        background: '#0469dc',
        color: '#fff',
        borderColor: '#0469dc',
        iconBody: '#ffffff34',
      };
      break;
    case 'info':
      colors = {
        outline: '#D7E9FE',
        outlineColor: '#5FA8FC',
        background: '#5FA8FC',
        color: '#fff',
        borderColor: '#5FA8FC',
        iconBody: '#ffffff34',
      };
      break;
    case 'secondary':
      colors = {
        outline: '#DEF8EA',
        outlineColor: '#2CC975',
        background: '#2CC975',
        color: '#fff',
        borderColor: '#2CC975',
        iconBody: '#ffffff34',
      };
      break;
    case 'success':
      colors = {
        outline: '#E2F4E6',
        outlineColor: '#399351',
        background: '#399351',
        color: '#fff',
        borderColor: '#399351',
        iconBody: '#ffffff34',
      };
      break;
    case 'warning':
      colors = {
        outline: '#fbe7d1',
        outlineColor: '#ed9e45',
        background: '#ed9e45',
        color: '#fff',
        borderColor: '#ed9e45',
        iconBody: '#ffffff34',
      };
      break;
    case 'premium':
      colors = {
        outline: '#E2D9FD',
        outlineColor: '#703FF3',
        background: '#703FF3',
        color: '#fff',
        borderColor: '#703FF3',
        iconBody: '#ffffff34',
      };
      break;
    case 'danger':
      colors = {
        outline: '#FBDADA',
        outlineColor: '#EB4747',
        background: '#EB4747',
        color: '#fff',
        borderColor: '#EB4747',
        iconBody: '#ffffff34',
      };
      break;
    case 'disabled':
      colors = {
        outline: '#D7DAE0',
        outlineColor: '#e4e4e4',
        background: '#e4e4e4',
        color: '#646777',
        borderColor: '#e4e4e4',
        iconBody: '#ffffff34',
      };
      break;
    default:
      colors = {
        outline: '#fff',
        background: '#fff',
        color: '#000',
        borderColor: '#D7DAE0',
        iconBody: '#F4F4F6',
      };
      break;
  }

  const style = {
    marginRight: 10,
    marginBottom: 10,
    padding: small ? '12px 12px 16px' : '20px 20px 26px',
    borderRadius: small ? 10 : 12,
    border: '1px solid',
    display: 'inline-block',
    height: small ? 96 : 156,
    width: small ? 190 : 270,
    background: outline ? colors.outline : colors.background,
    color: outline ? colors.outlineColor : colors.color,
    borderColor: colors.borderColor,
    boxSizing: 'border-box',
  };

  const iconBody = {
    background: colors.iconBody,
    borderRadius: small ? 8 : 12,
    height: small ? 26 : 35,
    width: small ? 26 : 35,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const pillBody = {
    background: '#ffffff34',
    borderRadius: 20,
    height: small ? 18 : 20,
    padding: small ? '0 6px' : '0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: small ? 9 : 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    ...(pillTone && PILL_TONES[pillTone]),
  };

  const iconStyle = {
    height: small ? 14 : 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div id={id} style={style}>
      <div className="flex space-between mb-2">
        <div style={iconBody}>
          <div style={iconStyle}>{icon}</div>
        </div>
        {pill && (
          <div
            id={pillTitle && id ? `${id}-pill` : undefined}
            style={{ ...pillBody, ...(pillTitle && { cursor: 'help' }) }}
          >
            {pill}
          </div>
        )}
        {pill && pillTitle && id && (
          <UncontrolledTooltip placement="top" target={`${id}-pill`}>
            {pillTitle}
          </UncontrolledTooltip>
        )}
      </div>
      <div style={{ display: 'inline-block', marginLeft: 5 }}>
        <span
          style={{
            opacity: 0.7,
            verticalAlign: 'sub',
            display: 'block',
            fontSize: small ? 10 : 12,
          }}
        >
          {title}
        </span>
        <span
          style={{
            verticalAlign: 'sub',
            display: 'block',
            fontSize: small ? 16 : 20,
            fontWeight: 600,
          }}
        >
          {value}
        </span>
        {text ? (
          <span
            style={{
              opacity: 0.7,
              verticalAlign: 'sub',
              display: 'block',
              fontSize: small ? 10 : 12,
            }}
          >
            {text}
          </span>
        ) : (
          <span>&nbsp;</span>
        )}
        {progress !== undefined && (
          <div
            style={{
              height: 4,
              width: small ? 90 : 120,
              borderRadius: 2,
              background: '#eee',
              marginTop: 4,
            }}
          >
            <div
              style={{
                height: 4,
                borderRadius: 2,
                width: `${Math.min(100, Math.max(0, progress))}%`,
                background: '#0469dc',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DisplayCounter;
